import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';
import { hasRouteAccess, getFirstAccessibleRoute } from '../../utils/routePermissionUtils';
import { getRoutePermissionConfig } from '../../config/routePermissions';
import PermissionDeniedPage from '../../pages/PermissionDeniedPage';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * PermissionProtectedRoute Component
 * 
 * Protects routes based on user permissions. If user doesn't have access:
 * 1. Displays a 403 Permission Denied page instead of redirecting
 * 
 * Works even if user manually changes the URL in the browser.
 */
const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentPermissions, loadingPermissions, currentArtistId, isInitialized, switchingArtist, availableArtists } = useArtist();
  const { userProfile } = useUser();
  
  // Track previous artist ID to detect artist switches
  const previousArtistIdRef = useRef<number | null>(null);
  // Track previous location to detect manual navigation
  const previousLocationRef = useRef<string>('');
  // Track if we've already redirected after artist switch to prevent loops
  const hasRedirectedAfterSwitchRef = useRef(false);
  // Track if we're checking redirect after artist switch (to show loading instead of 403)
  const [checkingRedirectAfterSwitch, setCheckingRedirectAfterSwitch] = useState(false);

  // Get system admin status
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null;
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();
  const isSystemAdmin = systemRole === 'system_admin';

  // Check if the selected artist is the logged-in person (owner)
  const selectedArtist = currentArtistId 
    ? availableArtists.find(a => a.artistId === currentArtistId)
    : null;
  const isSelectedArtistOwner = selectedArtist?.isOwner === true;

  // Check if permissions are actually loaded
  // We need: artist selected, not loading, and context initialized (artists loaded)
  // IMPORTANT: We need to distinguish between "permissions not loaded yet" vs "user has no permissions"
  // If loadingPermissions is false but permissions array is empty, we might still be waiting
  // So we'll be conservative: only make decisions when we're sure permissions have been fetched
  const permissionsReady = isInitialized && !loadingPermissions && currentArtistId !== null;
  
  // Check if user has access to the current route
  // IMPORTANT: Wait for permissions to actually load before making access decisions
  // Even system admins should wait - empty permissions array means permissions haven't loaded yet
  // Only make access decisions when we have actual permission data
  const canCheckAccess = permissionsReady && currentPermissions.length > 0;
  
  // For system admins accessing admin routes, we can allow even with empty permissions
  // But for all other cases, wait for permissions to load
  const isAdminRoute = getRoutePermissionConfig(location.pathname)?.category === 'admin';
  const canCheckAdminAccess = permissionsReady && isSystemAdmin && isAdminRoute;
  
  // Detect artist switch (for render-phase check to show loading)
  // Check if artist ID changed from previous render
  const artistSwitched = previousArtistIdRef.current !== null && 
                        previousArtistIdRef.current !== currentArtistId &&
                        currentArtistId !== null;

  // Check if this is a payouts route
  const isPayoutsRoute = location.pathname.startsWith('/payouts');
  
  // For payouts routes, require system admin AND selected artist must be owner
  // For other routes, use normal access check
  let hasAccess = false;
  if (canCheckAccess || canCheckAdminAccess) {
    if (isPayoutsRoute) {
      // Payouts routes require: system admin AND selected artist is owner
      hasAccess = isSystemAdmin && isSelectedArtistOwner;
    } else {
      // Other routes use normal permission check
      hasAccess = hasRouteAccess(location.pathname, currentPermissions, isSystemAdmin);
    }
  }

  // Track artist switches - update refs first
  useEffect(() => {
    // Detect when artist has switched (artist ID changed)
    const prevArtistId = previousArtistIdRef.current;
    const artistSwitchedInEffect = prevArtistId !== null && 
                          prevArtistId !== currentArtistId;
    
    // Update refs AFTER checking for switch
    previousArtistIdRef.current = currentArtistId;
    previousLocationRef.current = location.pathname;
    
    // Reset redirect flag when artist changes (new switch)
    if (artistSwitchedInEffect) {
      hasRedirectedAfterSwitchRef.current = false;
      setCheckingRedirectAfterSwitch(true); // Mark that we're checking redirect
      console.log('[PermissionProtectedRoute] Artist switch detected:', prevArtistId, '->', currentArtistId);
    }
  }, [currentArtistId, location.pathname]);

  // Separate effect to handle redirect AFTER permissions load
  useEffect(() => {
    // Only redirect if:
    // 1. We're checking redirect (artist switched)
    // 2. Artist switch is complete (switchingArtist is false)
    // 3. Permissions are loaded and ready
    // 4. We haven't already redirected for this switch
    if (checkingRedirectAfterSwitch && 
        !switchingArtist && 
        permissionsReady &&
        !hasRedirectedAfterSwitchRef.current) {
      
      // Check access to current route
      // For payouts routes, require system admin AND selected artist is owner
      const isPayoutsRoute = location.pathname.startsWith('/payouts');
      const currentRouteHasAccess = isPayoutsRoute
        ? (isSystemAdmin && isSelectedArtistOwner)
        : hasRouteAccess(location.pathname, currentPermissions, isSystemAdmin);
      
      if (!currentRouteHasAccess) {
        // Find first accessible route (handles empty permissions by returning /dashboard)
        const firstAccessibleRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        
        // Only redirect if we found a different route
        if (firstAccessibleRoute && firstAccessibleRoute !== location.pathname) {
          hasRedirectedAfterSwitchRef.current = true;
          setCheckingRedirectAfterSwitch(false); // Clear checking flag
          console.log('[PermissionProtectedRoute] Redirecting after artist switch from', location.pathname, 'to', firstAccessibleRoute);
          
          // Use React Router's navigate with replace to update URL without reload
          // The replace: true option ensures the URL changes in the browser without adding to history
          navigate(firstAccessibleRoute, { replace: true });
          
          return;
        } else {
          // No redirect needed (already on accessible route or no accessible route found)
          setCheckingRedirectAfterSwitch(false);
        }
      } else {
        // User has access to current route, clear checking flag
        setCheckingRedirectAfterSwitch(false);
      }
    } else if (!checkingRedirectAfterSwitch && hasRedirectedAfterSwitchRef.current) {
      // Reset redirect flag if we're no longer checking
      hasRedirectedAfterSwitchRef.current = false;
    }
  }, [checkingRedirectAfterSwitch, switchingArtist, permissionsReady, currentPermissions, isSystemAdmin, location.pathname, navigate]);

  // Show loading state while permissions are being fetched or artist is not selected
  // Wait until context is initialized and permissions are loaded
  if (!isInitialized || loadingPermissions || !currentArtistId) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // If we can't check access yet (permissions not loaded), wait
  // Don't make access decisions until permissions are loaded
  if (!canCheckAccess && !canCheckAdminAccess) {
    return <div className="flex items-center justify-center h-screen">Loading permissions...</div>;
  }

  // If user doesn't have access, check if we should redirect or show 403
  // Only check if we can actually verify access
  if (!hasAccess && (canCheckAccess || canCheckAdminAccess)) {
    // Don't show error if permissions are empty - wait for them to load
    if (currentPermissions.length === 0 && !isAdminRoute) {
      return <div className="flex items-center justify-center h-screen">Loading permissions...</div>;
    }
    
    // If we're checking redirect after artist switch, show loading instead of 403
    // This prevents showing 403 while the redirect is being processed
    // Also check if we should redirect (artist switched, permissions ready, no access)
    if (checkingRedirectAfterSwitch || hasRedirectedAfterSwitchRef.current) {
      return <div className="flex items-center justify-center h-screen">Redirecting...</div>;
    }
    
    // If artist switched and we should redirect, show loading (redirect will happen in useLayoutEffect)
    if (artistSwitched && 
        !switchingArtist && 
        permissionsReady && 
        !hasRedirectedAfterSwitchRef.current) {
      // Check access to current route
      // For payouts routes, require system admin AND selected artist is owner
      const isPayoutsRouteCheck = location.pathname.startsWith('/payouts');
      const currentRouteHasAccess = isPayoutsRouteCheck
        ? (isSystemAdmin && isSelectedArtistOwner)
        : hasRouteAccess(location.pathname, currentPermissions, isSystemAdmin);
      if (!currentRouteHasAccess) {
        const firstAccessibleRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        if (firstAccessibleRoute && firstAccessibleRoute !== location.pathname) {
          // Show loading - redirect will happen in useLayoutEffect
          return <div className="flex items-center justify-center h-screen">Redirecting...</div>;
        }
      }
    }
    
    // Show 403 Permission Denied page for normal navigation (not artist switch)
    // Artist switch redirects are handled in useEffect above and render phase check above
    return <PermissionDeniedPage />;
  }

  // User has access, render the protected content
  return <>{children}</>;
};

export default PermissionProtectedRoute;

